import { AxiosResponse } from "axios";
import { all, call, put, takeEvery, takeLatest } from "redux-saga/effects";

import * as actions from "./actions";
import * as types from "./types";
import * as services from "./service";
import * as alert from "../alert/actions";
import * as actionTypes from "./actionTypes";

function* postInboundWebhookSaga(action: types.PostInboundWebhookTriggersRequest) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IInboundWebhookSuccess | types.IApiError> = yield call(services.postInboundWebhook, action.payload);
        if (response.status === 201) {
            let dataPayload = response.data as types.IInboundWebhookSuccess;
            yield put(
                actions.postInboundWebhookSuccess({
                    inboundWebhook: dataPayload
                })
            );
            yield put(alert.enqueueSnackbar({ message: `Inbound webhook trigger successful. Timestamp: ${timestamp}`, options: { variant: "success" } }));

        }

        //Show IApiError
        if (response.status !== 201) {
            let apiError = response.data as types.IApiError;
            let errmsg = `StatusCode:${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}. Timestamp: ${timestamp}`;
            yield put(
                alert.enqueueSnackbar({
                    message: errmsg,
                    options: { variant: "error" },
                })
            );
            yield put(
                actions.postInboundWebhookFailure({
                    error: apiError,
                })
            );
        }
    } catch (e: any) {
        yield put(
            actions.postInboundWebhookFailure({
                error: e.message,
            })
        );
    }
}

function* postOutboundWebhookSaga(action: types.PostOutboundWebhookTriggersRequest) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IOutboundWebhookSuccess | types.IApiError> = yield call(services.postOutboundWebhook, action.payload);
        if (response.status === 201) {
            let dataPayload = response.data as types.IOutboundWebhookSuccess;
            yield put(
                actions.postOutboundWebhookSuccess({
                    outboundWebhook: dataPayload
                })
            );
            yield put(alert.enqueueSnackbar({ message: `Outbound webhook trigger successful. Timestamp: ${timestamp}`, options: { variant: "success" } }));

        }

        //Show IApiError
        if (response.status !== 201) {
            let apiError = response.data as types.IApiError;
            let errmsg = `StatusCode:${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}. Timestamp: ${timestamp}`;
            yield put(
                alert.enqueueSnackbar({
                    message: errmsg,
                    options: { variant: "error" },
                })
            );
            yield put(
                actions.postOutboundWebhookFailure({
                    error: apiError,
                })
            );
        }
    } catch (e: any) {
        yield put(
            actions.postOutboundWebhookFailure({
                error: e.message,
            })
        );
    }
}

function* fetchOutboundWebhookListSaga(action: types.FetchOutboundWebhookListReq) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IDataOutboundWebhookList> = yield call(services.getOutboundWebhookList, action.params);

        yield put(
            actions.fetchOutboundWebhookListSuccess({
                outboundWebhookList: response.data,
            })
        );

    } catch (e: any) {
        yield put(
            actions.fetchOutboundWebhookListFailure({
                error: e,
            })
        );
        yield put(alert.enqueueSnackbar({ message: `Could not load webhooks. Timestamp: ${timestamp}`, options: { variant: "error" } }));
    }
}

function* putInboundWebhookSaga({ eventId, payload }: types.PutInboundRequest) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IPutInboundSuccess | types.IApiError> = yield call(services.putInboundTrigger, eventId, payload);

        if (response.status === 204) {
            let dataPayload = response.data as types.IPutInboundSuccess;
            yield put(
                actions.putInboundWebhookSuccess({
                    status: dataPayload,
                })
            );
            let successMsg = `The inbound webhook was successfully edited. ID: ${eventId}. Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: successMsg, options: { variant: "success" } }));
        }

        if (response.status !== 204) {
            let apiError = response.data as types.IApiError;
            let errMsg = `StatusCode: ${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}. Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: errMsg, options: { variant: "error" } }));

        }
    } catch (e: any) {
        yield put(
            actions.putInboundWebhookFailure({
                error: e,
            })
        );
    }
}

function* putOutboundWebhookSaga({ eventId, payload }: types.PutOutboundRequest) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IPutOutboundSuccess | types.IApiError> = yield call(services.putOutboundTrigger, eventId, payload);

        if (response.status === 204) {
            let dataPayload = response.data as types.IPutOutboundSuccess;
            yield put(
                actions.putOutboundWebhookSuccess({
                    status: dataPayload,
                })
            );
            let successMsg = `The outbound webhook was successfully edited. ID: ${eventId}. Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: successMsg, options: { variant: "success" } }));
        }

        if (response.status !== 204) {
            let apiError = response.data as types.IApiError;
            let errMsg = `StatusCode: ${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}. Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: errMsg, options: { variant: "error" } }));

        }
    } catch (e: any) {
        yield put(
            actions.putOutboundWebhookFailure({
                error: e,
            })
        );
    }
}


function* deleteOutboundSaga(action: types.DeleteOutboundRequest) {
    const timestamp = new Date().toLocaleString();

    try {
        const response: AxiosResponse<types.IDeleteOutboundSuccess | types.IApiError> = yield call(services.deleteOutbound, action.params);

        if (response.status === 204) {
            yield put(
                actions.deleteOutboundSuccess({
                    success: true
                })
            );
            yield put(alert.enqueueSnackbar({ message: `The outbound webhook was successfully deleted. Timestamp: ${timestamp}`, options: { variant: "success" } }));

        }

        //Show IApiError
        if (response.status !== 204) {
            let apiError = response.data as types.IApiError;
            let errmsg = `StatusCode:${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}. Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: errmsg, options: { variant: "error" } }));
        }

    } catch (e: any) {
        yield put(
            actions.deleteOutboundFailure({
                error: e,
            })
        );
    }
}
/*
  Watcher saga - intercept the action fired from view
  Starts worker saga on latest dispatched `FETCH_ACCESSCONTROL_REQUEST` action.
  Allows concurrent increments.
*/
function* inboundWebhookSaga() {
    yield takeEvery(actionTypes.POST_INBOUND_WEBHOOK_REQUEST, postInboundWebhookSaga);
}

function* inboundPutWebhookSaga() {
    yield takeEvery(actionTypes.PUT_INBOUND_WEBHOOK_REQUEST, putInboundWebhookSaga);
}

function* outboundPutWebhookSaga() {
    yield takeEvery(actionTypes.PUT_OUTBOUND_WEBHOOK_REQUEST, putOutboundWebhookSaga);
}

function* deleteOutboundWebhookSaga() {
    yield takeEvery(actionTypes.DELETE_OUTBOUND_WEBHOOK_REQUEST, deleteOutboundSaga);
}

function* outboundWebhookListSaga() {
    yield takeLatest(actionTypes.FETCH_OUTBOUND_WEBHOOK_LIST_REQUEST, fetchOutboundWebhookListSaga);
}

function* outboundWebhookSaga() {
    yield takeEvery(actionTypes.POST_OUTBOUND_WEBHOOK_REQUEST, postOutboundWebhookSaga);
}

function* webhookSaga() {
    yield all([
        inboundWebhookSaga(),
        outboundWebhookListSaga(),
        inboundPutWebhookSaga(),
        deleteOutboundWebhookSaga(),
        outboundPutWebhookSaga(),
        outboundWebhookSaga(),])
}

export default webhookSaga;